import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useDownloadApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { PagingResponse } from 'entities'
import { Tag, TagForm, TagSearchForm } from 'entities/tag'

type TagsResponse = PagingResponse & {
    results: Tag[]
}

export function useFetchTagsApi(searchForm?: Form<TagSearchForm>): IndexApiSet<TagsResponse> & { execute: () => void; executeAndPageReset: () => void } {
    const apiPath = 'tags/'
    const api = useIndexApi<TagsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    const executeAndPageReset = (): void => {
        if (api.pageSet.page == 1) {
            execute()
        } else {
            api.pageSet.setPage(1)
        }
    }

    useEffectSkipFirst(() => {
        execute()
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        executeAndPageReset()
    }, [searchForm?.object])

    return { ...api, execute: execute, executeAndPageReset: executeAndPageReset }
}

export function useDownloadCsvTagsApi(searchForm: Form<TagSearchForm>): ApiSet<BaseResponse> & { execute: () => void } {
    const apiPath = 'tags/csv/'
    const api = useDownloadApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (): void => {
        api.execute(apiPath, 'tag', searchForm.object)
    }

    return { ...api, execute: execute }
}

export function usePostTagApi(): ApiSet<BaseResponse> & { execute: (form: Form<TagForm>) => void } {
    const apiSet = usePostApi<BaseResponse, TagForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<TagForm>) => {
        const apiPath = `tags/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutTagApi(): ApiSet<BaseResponse> & { execute: (form: Form<TagForm>) => void } {
    const apiSet = usePutApi<BaseResponse, TagForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<TagForm>) => {
        const apiPath = `tags/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutTagsApi(): ApiSet<BaseResponse> & { execute: (form: Form<TagForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, TagForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<TagForm[]>) => {
        const apiPath = `tags/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteTagApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `tags/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}
